import React, { useRef, useEffect } from 'react';
import * as echarts from 'echarts';
import { WeeklyEngagementRate } from '../../interfaces/typeCharts.interface';

type XAxisDataItem = { name: string; value: string; color?: string };

const ChartLine: React.FC<WeeklyEngagementRate & { ChartNumber: number; functionOnClick?: ( themeId: string ) => void }> = ( {
  xAxis,
  yAxis,
  series,
  ChartTitle,
  ChartNumber,
  functionOnClick,
} ) => {
  const chartRef = useRef<HTMLDivElement>( null );

  const nameToIdMap = React.useMemo( () => {
    if ( Array.isArray( xAxis.data ) ) {
      return Object.fromEntries(
        ( xAxis.data as XAxisDataItem[] ).map( item => {return [ item.name, item.value ];} )
      );
    }
    return {};
  }, [ xAxis.data ] );

  const xAxisForChart = {
    ...xAxis,
    data: Array.isArray( xAxis.data )
      ? ( xAxis.data as XAxisDataItem[] ).map( item => {return item.name;} )
      : xAxis.data,
    axisLine: {
      lineStyle: { color: '#888' },
    },
    axisLabel: {
      color: '#333',
      fontWeight: 'normal',
    },
  };

  const yAxisForChart = {
    ...yAxis,
    axisLine: {
      lineStyle: { color: '#888' },
    },
    axisLabel: {
      color: '#333',
      fontWeight: 'normal',
    },
    splitLine: {
      lineStyle: {
        color: '#eee',
        type: 'dashed',
      },
    },
  };

  useEffect( () => {
    let myChart: echarts.ECharts | null = null;
    const handleResize = () => {
      if ( myChart ) {
        myChart.resize();
      }
    };

    if ( chartRef.current ) {
      echarts.dispose( chartRef.current );
      if ( chartRef.current.clientHeight > 0 && chartRef.current.clientWidth > 0  ) {

        myChart = echarts.init( chartRef.current );

        const data = Array.isArray( xAxis.data )
          ? ( xAxis.data as XAxisDataItem[] ).map( ( item, index ) => {return {
            value: series[ 0 ]?.data?.[ index ] ?? 0,
            itemStyle: item.color ? { color: item.color } : undefined,
          };} )
          : series[ 0 ]?.data ?? [];

        const option = {
          backgroundColor: '#FFF',
          tooltip: {
            trigger: 'axis',
            backgroundColor: '#fff',
            borderColor: '#ccc',
            textStyle: { color: '#222' },
          },
          grid: {
            left: '5%',
            right: '5%',
            bottom: '10%',
            top: 60,
            containLabel: true,
          },
          xAxis: xAxisForChart,
          yAxis: yAxisForChart,
          series: [
            {
              name: series[ 0 ]?.[ 'name' ] ?? '',
              type: series[ 0 ]?.[ 'type' ] ?? 'bar',
              data,
              barWidth: '50%',
              itemStyle: {
                borderColor: '#FFF',
                borderWidth: 1,
              },
            },
          ],
        };

        if ( functionOnClick ) {
          myChart.off( 'click' );
          myChart.on( 'click', params => {
            if ( params.name && nameToIdMap[ params.name ] ) {
              functionOnClick( nameToIdMap[ params.name ] );
            }
          } );
        }

        myChart.setOption( option );
        window.addEventListener( 'resize', handleResize );
      }
    }

    return () => {
      if ( myChart ) {
        myChart.off( 'click' );
        myChart.dispose();
      }
      window.removeEventListener( 'resize', handleResize );
    };
  }, [ xAxisForChart, yAxisForChart, series, ChartTitle, ChartNumber, functionOnClick, nameToIdMap ] );


  return (
    <div ref={chartRef} style={{ width: '100%', height: '400px' }}/>
  );
};

export default ChartLine;
